import { Fragment } from 'react';

import { keineErlaubt } from '../../domain/kommentararten';

import {
  commentCount,
  criterionKey,
  isOverridden,
  isTaskDone,
  taskMax,
  taskScore,
  wordKey,
  type Assessment,
} from '../../domain/scoring';
import { toNumber } from '../../domain/points';
import { absaetzeMitWoertern } from '../../domain/woerter';
import { usePoints, useT } from '../../i18n';
import type { ChecklistItem, Criterion, Task } from '../../domain/types';
import { tapFeedback } from '../../platform/haptics';
import { useAppStore } from '../../store/useAppStore';
import { punktfeldBedienbar, punktfeldSichtbar, teilpunkteSichtbar } from '../../domain/bogenAnzeige';
import { Icon } from '../../ui/Icon';
import styles from './TaskCard.module.css';
import { HtmlText } from '../../ui/HtmlText';

interface Props {
  task: Task;
  index: number;
  assessment: Assessment;
  /** Nach dem Absenden: alles bleibt lesbar, nichts lässt sich mehr ändern. */
  locked: boolean;
  /** Der Bogenabzug läuft — dann steht der Kommentartext offen auf der Karte. */
  imAbzug: boolean;
}

/**
 * Der Kommentartext, ausgeschrieben — **nur im Bogenabzug** (Nutzer, 2026-11-01). Auf dem
 * Bogen steht nur „1 Kommentar"; der Abzug soll aber als Beleg zeigen, *was* notiert war.
 * Handschrift, Fotos, Audio und Video gehen als eigene Anhänge hinaus und bleiben gezählt.
 */
export function AbzugKommentar({ titel, text }: { titel: string; text: string }) {
  return (
    <div className={styles.abzugKommentar}>
      <div className={styles.abzugKommentarTitel}>{titel}</div>
      <div className={styles.abzugKommentarText}>{text}</div>
    </div>
  );
}

export function TaskCard({ task, index, assessment, locked, imAbzug }: Props) {
  const t = useT();
  const points = usePoints();
  const openSheet = useAppStore((s) => s.openSheet);

  const value = toNumber(taskScore(task, assessment));
  const max = toNumber(taskMax(task));
  const full = value >= max;
  const empty = value === 0;
  // „Offen" heißt: an dieser Aufgabe fehlt noch eine Entscheidung. Für Checklisten gibt es das
  // nicht — dort ist auch das leere Kästchen eine Wertung, und `isTaskDone` sagt darum immer ja.
  const open = !isTaskDone(task, assessment);
  const overridden = isOverridden(task, assessment);
  const anzeige = useAppStore((s) => s.bogenAnzeige);
  /**
   * Welche Kommentararten die Aufsicht zugelassen hat (`*_comments`, Auftrag des Nutzers,
   * 2026-09-18). Sind alle fünf aus, gibt es an dieser Aufgabe keinen Kommentarweg — dann steht
   * hier auch kein Knopf: ein Blatt ohne Reiter wäre ein Fehler, kein Zustand.
   */
  const arten = useAppStore((s) => s.kommentarArten);
  // ⚠️ Gezählt wird nur, was das Blatt auch zeigt. Sonst stünde hier „2 Kommentare" und beim
  // Öffnen wäre nichts zu sehen — s. `domain/scoring.ts::commentCount`.
  const comments = commentCount(assessment.comments[task.id], arten);
  const abzugText = imAbzug && arten.text ? (assessment.comments[task.id]?.text.trim() ?? '') : '';

  // Breitester Wert, den das Feld je zeigt: ganzzahliger Anteil des Höchstwerts mit zwei
  // Nachkommastellen. Reserviert die Breite, sonst bricht der Aufgabentext links davon bei
  // jedem Häkchen anders um und die Karte springt in der Höhe.
  const widestValue = `${Math.trunc(max)}${t.format.decimal}00`;

  const hint = overridden
    ? t.bogen.adjusted
    : full
      ? t.bogen.fullScore
      : empty
        ? t.bogen.noScore
        : t.bogen.reduced;

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <div className={styles.headMeta}>
            <span className={styles.number}>
              {t.bogen.task} {index + 1}
            </span>
            <span className={styles.type}>{task.typeLabel}</span>
            <div className={styles.spacer} />
            {!keineErlaubt(arten) && (
              <button
                type="button"
                className={`tap ${styles.commentButton}`}
                data-has={comments > 0}
                onClick={() => openSheet({ kind: 'comment', taskId: task.id })}
                disabled={locked}
              >
                <Icon name="comment" size={14} />
                <span>{comments ? t.bogen.comments(comments) : t.bogen.comment}</span>
              </button>
            )}
          </div>
          <div className={styles.title}><HtmlText text={task.title} /></div>
        </div>

        {/*
          * `show_task_points` blendet das Feld aus, `editable_task_points` macht es zur reinen
          * Anzeige — beides nicht bei globaler Bewertung, dort ist es der einzige Weg zu Punkten
          * (`domain/bogenAnzeige.ts`). `colorful_text` aus: alle Felder sehen gleich aus.
          */}
        {punktfeldSichtbar(task, anzeige) && (
          <button
            type="button"
            className={`tap ${styles.score}`}
            data-empty={empty}
            data-open={open}
            data-farbig={anzeige.farbig}
            data-fest={!punktfeldBedienbar(task, anzeige)}
            onClick={() => openSheet({ kind: 'score', taskId: task.id })}
            disabled={locked || !punktfeldBedienbar(task, anzeige)}
          >
            <span className={styles.scoreLine}>
              <span className={styles.scoreValue}>
                <span className={styles.scoreMeasure} aria-hidden="true">
                  {widestValue}
                </span>
                <span>{points(value)}</span>
              </span>
              <span className={styles.scoreMax}>/ {points(max)}</span>
            </span>
            <span className={styles.scoreHint}>{hint}</span>
          </button>
        )}
      </div>

      {task.scoring.kind === 'checklist' && (
        <ChecklistBody
          task={task}
          items={task.scoring.items}
          assessment={assessment}
          locked={locked}
        />
      )}
      {task.scoring.kind === 'global' && (
        <GlobalBody
          task={task}
          body={task.scoring.body}
          assessment={assessment}
          locked={locked}
        />
      )}
      {task.scoring.kind === 'criteria' && (
        <CriteriaBody
          task={task}
          criteria={task.scoring.criteria}
          assessment={assessment}
          locked={locked}
        />
      )}
      {abzugText && <AbzugKommentar titel={t.bogen.comment} text={abzugText} />}
    </div>
  );
}

function ChecklistBody({
  task,
  items,
  assessment,
  locked,
}: {
  task: Task;
  items: ChecklistItem[];
  assessment: Assessment;
  locked: boolean;
}) {
  const t = useT();
  const points = usePoints();
  const toggle = useAppStore((s) => s.toggleChecklistItem);
  const haptics = useAppStore((s) => s.afterSubmit.haptics);
  const anzeige = useAppStore((s) => s.bogenAnzeige);
  const checked = assessment.checked[task.id] ?? [];

  return (
    <div className={styles.checklist}>
      {items.map((item, i) => {
        const on = !!checked[i];
        return (
          <button
            key={i}
            type="button"
            className={`tap ${styles.checkRow}`}
            data-on={on}
            aria-pressed={on}
            onClick={() => {
              tapFeedback(haptics);
              toggle(task.id, i);
            }}
            disabled={locked}
          >
            <span className={styles.checkBox}>
              <Icon name="check" size={13} color={on ? '#ffffff' : 'transparent'} />
            </span>
            <HtmlText className={styles.checkText} text={item.text} />
            {teilpunkteSichtbar(anzeige) && (
              <span className={styles.checkWorth}>{t.bogen.points(points(item.points))}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function GlobalBody({
  task,
  body,
  assessment,
  locked,
}: {
  task: Task;
  body: string;
  assessment: Assessment;
  locked: boolean;
}) {
  const toggleWord = useAppStore((s) => s.toggleWord);

  /*
   * ⚠️ **Der Orientierungstext kann mehrere Absätze haben.** Im Classic-Bestand trägt eine
   * global bewertete Aufgabe oft mehrere Antwortzeilen, und der Classic-Client zeigt ihre Texte
   * untereinander als einen Block (`server/classic/abbildung.ts::blockAusZeilen`, getrennt
   * durch `\n`).
   *
   * **Die Wortnummer läuft über den ganzen Block weiter**, nicht je Absatz von vorn: sie ist
   * der Schlüssel in `pickedWords` und muss zu der Zählung passen, mit der das Zurückschreiben
   * die Marken auf die Classic-Zeilen verteilt (`classic/kennung.ts::verteileWortindexe`).
   * Zählte hier jeder Absatz von null, landeten die Marken am Classic-Server auf falschen Wörtern.
   *
   * Die Zerlegung selbst steht deshalb in `domain/woerter.ts` — dieselbe Funktion zählt beim
   * Download die Wörter jeder Antwortzeile.
   */
  const absaetze = absaetzeMitWoertern(body);
  let laufend = 0;

  return (
    <div className={styles.body}>
      {absaetze.map((woerter, nr) => {
        const beginn = laufend;
        laufend += woerter.length;
        /*
         * ⚠️ **Ein Absatz ohne Wörter behält seine Zeile, aber keine Antippfläche.** Bis zum
         * 2026-09-13 stand hier auch dann ein Wort — ein leeres. Es war unsichtbar, liess sich
         * aber antippen und setzte eine Marke auf Wort 0, die es gar nicht gibt. Bei den
         * Prüfungen 70 und 73 ist das der Normalfall: dort ist der ganze Orientierungstext
         * leer.
         */
        if (woerter.length === 0) {
          return (
            <p className={styles.paragraph} key={nr}>
              {' '}
            </p>
          );
        }
        return (
          <p className={styles.paragraph} key={nr}>
            {woerter.map((word, i) => {
              const index = beginn + i;
              const on = !!assessment.pickedWords[wordKey(task.id, index)];
              return (
                // Das Leerzeichen hinter der Marke ist die Umbruchstelle — ohne einen echten
                // Textknoten dazwischen stünde der ganze Absatz auf einer Zeile.
                <Fragment key={i}>
                  <span
                    className={`tap ${styles.word}`}
                    data-on={on}
                    onClick={locked ? undefined : () => toggleWord(task.id, index)}
                  >
                    {word}
                  </span>{' '}
                </Fragment>
              );
            })}
          </p>
        );
      })}
    </div>
  );
}

function CriteriaBody({
  task,
  criteria,
  assessment,
  locked,
}: {
  task: Task;
  criteria: Criterion[];
  assessment: Assessment;
  locked: boolean;
}) {
  const t = useT();
  const points = usePoints();
  const openSheet = useAppStore((s) => s.openSheet);
  const anzeige = useAppStore((s) => s.bogenAnzeige);

  return (
    <div className={styles.criteria}>
      {criteria.map((criterion, i) => {
        const chosen = assessment.criteria[criterionKey(task.id, i)];
        const set = chosen != null;
        const option = set ? criterion.options[chosen] : null;
        const maxPoints = Math.max(...criterion.options.map((o) => o.points));

        return (
          <button
            key={i}
            type="button"
            className={`tap ${styles.criterionRow}`}
            data-set={set}
            onClick={() => openSheet({ kind: 'criterion', taskId: task.id, index: i })}
            disabled={locked}
          >
            <HtmlText className={styles.criterionLabel} text={criterion.label} />
            <span className={styles.criterionValue}>
              <span className={styles.criterionChoice}>
                {option ? <HtmlText text={option.label} /> : t.bogen.pending}
              </span>
              {teilpunkteSichtbar(anzeige) && (
                <span className={styles.criterionPoints}>
                  {option
                    ? t.bogen.ofPoints(points(option.points), points(maxPoints))
                    : t.bogen.possible(points(maxPoints))}
                </span>
              )}
            </span>
            <span className={styles.criterionMark}>
              {set && <Icon name="check" size={15} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}
